<template>
  <aside class="preliminaries-page">
    <div>
      <DocumentPageTitle
        :word-count="totalWords"
        :word-limit="wordLimit"
        title="Preliminaries"
      />

      <h2 id="list-of-abbreviations">
        List of abbreviations
      </h2>

      <AppTable class="max-w-[640px]">
        <AppTableHeader>
          <th class="w-[120px]">
            Abbreviations
          </th>

          <th>Full Name</th>
        </AppTableHeader>

        <AppTableBody
          :loading="planStore.loading"
          :colspan="2"
          :no-results="planContentKey.abbreviations.length === 0"
          no-results-text="No abbreviations defined"
        >
          <tr
            v-for="(abbreviation, index) in planContentKey.abbreviations"
            :key="index"
          >
            <td class="align-top">
              {{ abbreviation.abbreviation }}
            </td>

            <td>
              <div class="flex items-center justify-between">
                {{ abbreviation.fullName }}
                <DocumentActions
                  subject="abbreviation"
                  @edit-clicked="formModal.open('abbreviation', abbreviation)"
                  @delete-clicked="deleteModal.open('abbreviation', abbreviation)"
                />
              </div>
            </td>
          </tr>
        </AppTableBody>
      </AppTable>

      <AppButtonGroup class="mb-10">
        <AppButton
          class="btn-outline btn-secondary btn-dashed"
          @click="formModal.open('abbreviation', abbreviationSchema)"
        >
          <FontAwesomeIcon :icon="faPlus" />
          Add an Abbreviation
        </AppButton>

        <AppButton
          class="btn-outline btn-secondary btn-dashed"
          @click="showBatchImport = true"
        >
          <FontAwesomeIcon :icon="faUpload" />
          Batch upload
        </AppButton>
      </AppButtonGroup>

      <div
        v-for="official in officials"
        :key="official.key"
        class="mb-12"
      >
        <TextEditorInput
          :id="official.id"
          v-model="planContentKey[official.key].message"
          :label="`Message from the ${official.label}`"
          :word-limit="wordLimit"
          :total-word-count="totalWords"
          class="!mb-8"
          @update:word-count="handleWordCountUpdate"
        />

        <div
          class="w-full flex gap-3"
          :class="getOfficialPhoto(official.key) ? 'justify-between' : ''"
        >
          <div
            class="flex flex-col items-center"
            :class="[
              getOfficialPhoto(official.key) ? 'order-3 w-[180px]' : '', (!getOfficialPhoto(official.key) && (getOfficialSignature(official.key) || planContentKey[official.key].name || planContentKey[official.key].title)) ? 'order-2' : ''
            ]"
          >
            <img
              v-if="getOfficialPhoto(official.key)"
              :src="getOfficialPhoto(official.key)"
              class="h-[180px] w-[180px] rounded-full mb-2 object-cover"
              alt="Official Photo"
            >

            <AppButton
              class="btn-outline btn-secondary btn-dashed h-fit w-fit"
              @click="openDetailsModal(official)"
            >
              {{ getOfficialPhoto(official.key) ? 'Change photo' : `Upload a photo of the ${official.label}` }}
            </AppButton>
          </div>

          <div :class="!getOfficialPhoto(official.key) && !getOfficialSignature(official.key) && !planContentKey[official.key].name && !planContentKey[official.key].title ? 'flex gap-3' : ''">
            <img
              v-if="getOfficialSignature(official.key)"
              :src="getOfficialSignature(official.key)"
              class="h-[180px] mb-2 border border-editor-chrome-light object-contain"
              alt="Signature"
            >

            <AppButton
              class="btn-outline btn-secondary btn-dashed"
              :class="getOfficialSignature(official.key) || getOfficialPhoto(official.key) || planContentKey[official.key].name || planContentKey[official.key].title ? 'mb-4' : ''"
              @click="openSignatureModal(official)"
            >
              {{ getOfficialSignature(official.key) ? 'Change signature' : 'Upload a signature' }}
            </AppButton>

            <h3 v-if="planContentKey[official.key].name">
              {{ planContentKey[official.key].name }}
            </h3>

            <p
              v-if="planContentKey[official.key].title"
              class="italics mb-0"
            >
              {{ planContentKey[official.key].title }}
            </p>

            <AppButton
              class="btn-outline btn-secondary btn-dashed"
              :class="planContentKey[official.key].title || planContentKey[official.key].name ? 'mt-2' : ''"
              @click="openDetailsModal(official)"
            >
              {{ `Edit ${official.label}'s details` }}
            </AppButton>
          </div>
        </div>
      </div>
    </div>

    <AppModal
      v-if="formModal.type === 'abbreviation'"
      :title="formModal.isEditing ? 'Edit Abbreviation' : 'Add Abbreviation'"
      :disabled="formModal.processing"
      @close="handleCancel"
    >
      <template #body>
        <AbbreviationForm
          v-model="formModal.data"
          :errors="abbreviationErrors"
          @submit="saveAbbreviation"
        />
      </template>

      <template #footer>
        <ButtonSubmitRow
          :processing="formModal.processing"
          @submit-clicked="saveAbbreviation"
          @cancel-clicked="handleCancel"
        />
      </template>
    </AppModal>

    <AppModal
      v-if="formModal.type === 'details'"
      :title="`${formModal.context.official.label}'s details`"
      :disabled="formModal.processing"
      @close="formModal.close"
    >
      <template #body>
        <OfficialDetailsForm
          v-model:selectedFile="selectedFile"
          v-model:formData="formModal.data"
          :current-photo="getOfficialPhoto(formModal.context.official.key)"
          :upload-subject="formModal.context.official.label"
          @submit="saveDetails"
        />
      </template>

      <template #footer>
        <ButtonSubmitRow
          submit-label="Save Photo"
          :processing="formModal.processing"
          @cancel-clicked="formModal.close"
          @submit-clicked="saveDetails"
        />
      </template>
    </AppModal>

    <AppModal
      v-if="formModal.type === 'signature'"
      :title="`Upload the ${formModal.context.official.label}'s signature`"
      :disabled="formModal.processing"
      @close="formModal.close"
    >
      <template #body>
        <OfficialSignatureForm
          v-model:selectedFile="selectedFile"
          v-model:formData="formModal.data"
          :current-signature="getOfficialSignature(formModal.context.official.key)"
          @submit="saveSignature"
        />
      </template>

      <template #footer>
        <ButtonSubmitRow
          :processing="formModal.processing"
          @cancel-clicked="formModal.close"
          @submit-clicked="saveSignature"
        />
      </template>
    </AppModal>

    <ConfirmationModal
      v-if="deleteModal.type === 'abbreviation'"
      :processing="deleteModal.processing"
      intent="danger"
      title="Delete Abbreviation"
      @close="deleteModal.close"
      @confirm="deleteAbbreviation"
    >
      Are you sure you want to delete the abbreviation <strong>{{ deleteModal.data.abbreviation }}</strong>?
    </ConfirmationModal>

    <BatchImportModal
      v-if="showBatchImport"
      :definition="abbreviationsImport"
      @close="showBatchImport = false"
    />
  </aside>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { computed, ref } from 'vue';

import { faPlus, faUpload } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import { AppButton, AppButtonGroup, AppModal } from '@/components/app';
import { ButtonSubmitRow } from '@/components/buttons';
import { ConfirmationModal } from '@/components/modals';
import { DocumentPageTitle, DocumentActions } from '@/components/plan-document/components';
import { TextEditorInput } from '@/components/form-inputs';
import { AbbreviationForm, OfficialDetailsForm, OfficialSignatureForm } from '@/components/plan-document/forms';
import { BatchImportModal } from '@/components/plan-document/import';
import { AppTable, AppTableBody, AppTableHeader } from '@/components/tables';

import { useHttp, useModal, useToasts, useTinyMCEWordCount } from '@/composables';
import { abbreviationsImport } from '@/composables/import';
import { usePlanStore } from '@/store';
import { PlanOfficials } from '@/const.js';
import { useRoute } from 'vue-router';

const planStore = usePlanStore();
const route = useRoute();
const toasts = useToasts();

const wordLimit = 3000;
const abbreviationErrors = ref({
  abbreviation: null,
  fullName: null,
});

const planContentKey = computed(() => {
  return planStore.content.preliminaries || null;
});

const officials = [
  { label: 'Mayor', key: 'mayor', id: 'message-from-the-mayor' },
  { label: 'Municipal Manager', key: 'municipalManager', id: 'message-from-the-municipal-manager' },
];

const { totalWords, updateCount } = useTinyMCEWordCount(wordLimit, officials.map(o => o.id));

const handleWordCountUpdate = ({ id, count }) => {
  updateCount(id, count);
};

// Check which official is needed for the photo
const getOfficialPhoto = (key) => {
  if (key === PlanOfficials.Mayor) return planStore.mayorPhoto;
  if (key === PlanOfficials.MunicipalManager) return planStore.municipalManagerPhoto;

  return null;
};

// Check which official is needed for the signature
const getOfficialSignature = (key) => {
  if (key === PlanOfficials.Mayor) return planStore.mayorSignature;
  if (key === PlanOfficials.MunicipalManager) return planStore.municipalManagerSignature;

  return null;
};

const formModal = useModal();
const deleteModal = useModal();

const showBatchImport = ref(false);

const abbreviationSchema = { id: null, abbreviation: '', fullName: '' };
const selectedFile = ref(null);

const saveAbbreviation = async () => {
  formModal.processing = true;
  const { data, isEditing } = formModal;

  if (!data.abbreviation || !data.fullName) {
    if (!data.abbreviation) {
      abbreviationErrors.value.abbreviation = 'This is a required field';
    }

    if (!data.fullName) {
      abbreviationErrors.value.fullName = 'This is a required field';
    }

    formModal.processing = false;
    return;
  }

  try {
    if (isEditing) {
      const index = planContentKey.value.abbreviations.findIndex(a => a.id === data.id);

      if (index !== -1) {
        planContentKey.value.abbreviations[index] = { ...data };
      }
    } else {
      planContentKey.value.abbreviations.push({ ...data, id: Date.now() });
    }

    planContentKey.value.abbreviations = planContentKey.value.abbreviations.sort((a, b) => a.abbreviation.toLowerCase().localeCompare(b.abbreviation.toLowerCase()));

    formModal.close();
  } catch (error) {
    console.error(error);
  }
};

const deleteAbbreviation = () => {
  deleteModal.processing = true;

  planContentKey.value.abbreviations = planContentKey.value.abbreviations
    .filter(item => item.id !== deleteModal.data.id)
    .sort((a, b) => a.abbreviation.toLowerCase().localeCompare(b.abbreviation.toLowerCase()));

  deleteModal.close();
};

const openDetailsModal = (official) => {
  selectedFile.value = null;

  const currentData = planContentKey.value[official.key];

  const payload = {
    name: currentData.name || '',
    title: currentData.title || '',
    removePhoto: false,
  };

  formModal.open('details', payload, { official });
};

const openSignatureModal = (official) => {
  selectedFile.value = null;

  const payload = {
    removePhoto: false,
  };

  formModal.open('signature', payload, { official });
};

const saveDetails = async () => {
  formModal.processing = true;
  const { data, context } = formModal;

  const target = planContentKey.value[context.official.key];

  target.name = data.name;
  target.title = data.title;

  const isMayor = context.official.key === PlanOfficials.Mayor;

  if (selectedFile.value) {
    const formData = new FormData();
    formData.append('image', selectedFile.value);

    const response = await useHttp().post(`/municipalities/${route.params.municipality}/plan/${isMayor ? 'mayor' : 'municipal-manager'}/photo`, formData);

    if (response.statusCode === HttpStatusCode.UnprocessableEntity) {
      toasts.error('The photo exceeds the 10MB size limit. Please upload a smaller image.');
      formModal.processing = false;
      return;
    }

    if (response.statusCode === HttpStatusCode.Ok) {
      isMayor ? (planStore.mayorPhoto = response.data.mayorPhoto) : (planStore.municipalManagerPhoto = response.data.municipalManagerPhoto);
    }
  } else if (data.removePhoto) {
    const response = await useHttp().delete(`/municipalities/${route.params.municipality}/plan/${isMayor ? 'mayor' : 'municipal-manager'}/photo`);

    if (response.statusCode === HttpStatusCode.Ok) {
      isMayor ? (planStore.mayorPhoto = null) : (planStore.municipalManagerPhoto = null);
    }
  }

  formModal.close();
};

const saveSignature = async () => {
  formModal.processing = true;
  const { data, context } = formModal;

  const isMayor = context.official.key === PlanOfficials.Mayor;

  if (selectedFile.value) {
    const formData = new FormData();
    formData.append('image', selectedFile.value);

    const response = await useHttp().post(`/municipalities/${route.params.municipality}/plan/${isMayor ? 'mayor' : 'municipal-manager'}/signature`, formData);

    if (response.statusCode === HttpStatusCode.UnprocessableEntity) {
      toasts.error('The signature exceeds the 10MB size limit. Please upload a smaller image.');
      formModal.processing = false;
      return;
    }

    if (response.statusCode === HttpStatusCode.Ok) {
      isMayor ? (planStore.mayorSignature = response.data.mayorSignature) : (planStore.municipalManagerSignature = response.data.municipalManagerSignature);
    }
  } else if (data.removePhoto) {
    const response = await useHttp().delete(`/municipalities/${route.params.municipality}/plan/${isMayor ? 'mayor' : 'municipal-manager'}/signature`);

    if (response.statusCode === HttpStatusCode.Ok) {
      isMayor ? (planStore.mayorSignature = null) : (planStore.municipalManagerSignature = null);
    }
  }

  formModal.close();
};

const handleCancel = () => {
  abbreviationErrors.value = {
    abbreviation: null,
    fullName: null,
  };
  formModal.close();
};

</script>
